// @ts-strict-ignore
/* eslint-disable no-restricted-imports */
// eslint-disable-next-line @fullscript/cross-reference
import * as remix from "@remix-run/react";
import * as router from "react-router-dom";

// Copied over from react-router-dom internals so that this useMatch wrapper respects the same types as react-router-dom
type ParamParseFailed = {
  failed: true;
};

// Copied over from react-router-dom internals so that this useMatch wrapper respects the same types as react-router-dom
type ParamParseSegment<Segment extends string> =
  Segment extends `${infer LeftSegment}/${infer RightSegment}`
    ? ParamParseSegment<LeftSegment> extends infer LeftResult
      ? ParamParseSegment<RightSegment> extends infer RightResult
        ? LeftResult extends string
          ? RightResult extends string
            ? LeftResult | RightResult
            : LeftResult
          : RightResult extends string
            ? RightResult
            : ParamParseFailed
        : ParamParseFailed
      : ParamParseSegment<RightSegment> extends infer RightResult
        ? RightResult extends string
          ? RightResult
          : ParamParseFailed
        : ParamParseFailed
    : Segment extends `:${infer Remaining}`
      ? Remaining
      : ParamParseFailed;

type ParamParseKey<Segment extends string> =
  ParamParseSegment<Segment> extends string ? ParamParseSegment<Segment> : string;

interface PathPattern<Path extends string = string> {
  /**
   * A string to match against a URL pathname. May contain `:id`-style segments
   * to indicate placeholders for dynamic parameters. May also end with `/*` to
   * indicate matching the rest of the URL pathname.
   */
  path: Path;
  /**
   * Should be `true` if the static portions of the `path` should be matched in
   * the same case.
   */
  caseSensitive?: boolean;
  /**
   * Should be `true` if this pattern should match the entire URL pathname.
   */
  end?: boolean;
}

/**
 * A hook wrapping useMatch from Remix and react-router-dom
 * It can be used in interchangeably in a Remix or react-router-dom based SPA and work as expected
 * Means that devs don't need to worry about where an app is running and what API to use
 *
 * @returns - either a Remix or react-router-dom based match object
 */
const useMatch = <ParamKey extends ParamParseKey<Path>, Path extends string>(
  pattern: PathPattern<Path> | Path
): router.PathMatch<ParamKey> | null => {
  let routerMatch;
  let remixMatch;
  let routerError: Error;
  let remixError: Error;

  // Order is important here, we default to trying the react-router-dom version
  // If it does not throw, we know we are operating in some sort of nested router during the Remix migration
  // Otherwise if it fails, we know we are on a Remix only page
  try {
    routerMatch = router.useMatch(pattern);
  } catch (e) {
    routerError = e;
  }

  try {
    remixMatch = remix.useMatch(pattern);
  } catch (e) {
    // If there's no match at all, we must throw
    if (routerMatch === undefined && remixMatch === undefined) {
      remixError = e;
    }
  }

  if (routerError && remixError) {
    throw routerError;
  }

  if (routerMatch !== undefined) {
    return routerMatch;
  }

  return remixMatch;
};

export { useMatch };
